/**
 * Provides an administrative interface for configuring the Idea Tracker
 * dashboard widget. Allows administrators to manage the Power BI report
 * endpoint used to surface idea tracking and innovation metrics within
 * the application. Supports loading existing widget configurations,
 * updating analytics integration settings, tracking unsaved changes,
 * and persisting configuration updates to ensure the widget displays
 * the correct reporting and business intelligence content.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.ideatracker";

const IdeaTrackerConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [ideaTrackerPowerBiUrl, setIdeaTrackerPowerBiUrl] =
    useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const ideaTrackerConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(ideaTrackerConfig);

    setIdeaTrackerPowerBiUrl(
      ideaTrackerConfig.PowerBIIdeaTrackerUrl || "",
    );
  };

  const hasChanges =
    !!savedConfig &&
    ideaTrackerPowerBiUrl !==
      (savedConfig.PowerBIIdeaTrackerUrl || "");

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          PowerBIIdeaTrackerUrl: ideaTrackerPowerBiUrl,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setIdeaTrackerPowerBiUrl(
      savedConfig.PowerBIIdeaTrackerUrl || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Idea Tracker Power BI URL
        </span>

        <input
          type="text"
          value={ideaTrackerPowerBiUrl}
          onChange={(e) => setIdeaTrackerPowerBiUrl(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Power BI URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default IdeaTrackerConfig;